import { FC, ReactElement, useEffect, useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import { Link } from 'react-router-dom';

import { useTranslate } from 'hooks/useTranslate';
import { formatNumber } from 'utils/formats';
import { IncentivesMarketBeneficiary } from 'modules/incentivesMarket/types';
import { State } from 'redux/initialState';

import { useGetBeneficiaries } from 'modules/incentivesMarket/hooks/useGetBeneficiaries';
import CustomLoading from 'modules/_shared/components/CustomLoading';
import Switch from 'modules/_shared/components/Switch';
import DataTablePagination from 'modules/_shared/components/Tables/DataTablePagination';
import {
  hasPaginationChanged,
  mapPaginationToPaginationData,
} from 'modules/_shared/components/Pagination/paginationUtils';
import Button, { ButtonVariants } from 'modules/_shared/components/Buttons';

/* eslint-disable no-unused-vars -- callback param names in type definitions */
type IncentivesMarketAdminPanelBeneficiaryAccessProps = {
  societyId: string;
  onToggleBeneficiaryAccess: (
    beneficiaryIds: string[],
    enabled: boolean
  ) => void | Promise<void>;
  isLoading?: boolean;
};
/* eslint-enable no-unused-vars */

const INITIAL_PAGE = 1;
const INITIAL_ROWS_PER_PAGE = 10;

const getPartyImage = (beneficiary: IncentivesMarketBeneficiary) => beneficiary?.image || '';

const getPartyInitials = (beneficiary: IncentivesMarketBeneficiary) => {
  const name = beneficiary?.name || '';
  if (!name) return '-';
  return name
    .split(' ')
    .map((n) => n[0])
    .join('')
    .slice(0, 2)
    .toUpperCase();
};

const IncentivesMarketAdminPanelBeneficiaryAccess: FC<
  IncentivesMarketAdminPanelBeneficiaryAccessProps
> = ({
  societyId,
  onToggleBeneficiaryAccess,
  isLoading: isParentLoading = false,
}) => {
  const { t } = useTranslate();

  const user = useSelector((state: State) => state.user);
  const actualSociety = useSelector(
    (state: State) => state.society?.actualSociety
  );
  const isAdmin = useSelector(
    (state: State) => state.society?.role?.isAdmin ?? false
  );
  const reduxPartners = useSelector(
    ({ partners }: State) => (partners ?? []) as IncentivesMarketBeneficiary[]
  );

  const [searchText, setSearchText] = useState('');
  const [selectedBeneficiaryIds, setSelectedBeneficiaryIds] = useState<string[]>([]);
  const [currentPage, setCurrentPage] = useState(INITIAL_PAGE);
  const [rowsPerPage, setRowsPerPage] = useState(INITIAL_ROWS_PER_PAGE);
  const [paginationData, setPaginationData] = useState({
    customItemPerPage: INITIAL_ROWS_PER_PAGE,
    itemPerPage: INITIAL_ROWS_PER_PAGE,
    currentPage: INITIAL_PAGE,
    totalItems: 0,
    totalPages: 0,
  });
  const [listRefreshKey, setListRefreshKey] = useState(0);
  const [isToggleAccessLoading, setIsToggleAccessLoading] = useState(false);

  const { data, pagination, isLoading, isError } = useGetBeneficiaries({
    ...(!isAdmin && !actualSociety?.hasAccessCaptable && { userId: user._id }),
    societyId,
    page: currentPage,
    items: rowsPerPage,
    searchTerm: searchText,
    refreshKey: listRefreshKey,
  });

  useEffect(() => {
    if (pagination && hasPaginationChanged(pagination, paginationData)) {
      setPaginationData((prev) => ({
        ...prev,
        ...mapPaginationToPaginationData(pagination),
      }));
    }
  }, [pagination]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    setCurrentPage(INITIAL_PAGE);
  }, [searchText]);

  useEffect(() => {
    setSelectedBeneficiaryIds([]);
  }, [currentPage, rowsPerPage]);

  const reduxById = useMemo(
    () => new Map(reduxPartners.map((p) => [p._id, p])),
    [reduxPartners]
  );

  const rows = useMemo(
    () =>
      data.map((p) => {
        const redux = reduxById.get(p._id);
        const enabled =
          typeof p.configuration?.incentivesMarket?.enabled === 'boolean'
            ? p.configuration.incentivesMarket.enabled
            : redux?.configuration?.incentivesMarket?.enabled ?? false;
        return { beneficiary: p, equityAccessEnabled: enabled };
      }),
    [data, reduxById]
  );

  const totalPartners =
    pagination?.totalItems ?? paginationData.totalItems ?? 0;

  const enabledCount = useMemo(
    () =>
      reduxPartners.filter((p) => p.configuration?.incentivesMarket?.enabled)
        .length,
    [reduxPartners]
  );

  const handleToggleBeneficiary = async (beneficiaryId: string, enabled: boolean) => {
    setIsToggleAccessLoading(true);
    try {
      await onToggleBeneficiaryAccess([beneficiaryId], enabled);
      setListRefreshKey((k) => k + 1);
    } finally {
      setIsToggleAccessLoading(false);
    }
  };

  const handleSelectBeneficiary = (beneficiaryId: string) => {
    setSelectedBeneficiaryIds((prev) =>
      prev.includes(beneficiaryId)
        ? prev.filter((id) => id !== beneficiaryId)
        : [...prev, beneficiaryId]
    );
  };

  const handleSelectAll = () => {
    if (selectedBeneficiaryIds.length === rows.length) {
      setSelectedBeneficiaryIds([]);
    } else {
      setSelectedBeneficiaryIds(rows.map(({ beneficiary }) => beneficiary._id));
    }
  };

  const handleBulkToggle = async (enabled: boolean) => {
    if (selectedBeneficiaryIds.length === 0) return;
    setIsToggleAccessLoading(true);
    try {
      await onToggleBeneficiaryAccess(selectedBeneficiaryIds, enabled);
      setListRefreshKey((k) => k + 1);
      setSelectedBeneficiaryIds([]);
    } finally {
      setIsToggleAccessLoading(false);
    }
  };

  const handleChangePage = (newPage: number) => {
    setCurrentPage(newPage);
  };

  const handleChangeRowsPerPage = (newRowsCount: number) => {
    setRowsPerPage(newRowsCount);
    setCurrentPage(INITIAL_PAGE);
  };

  const switchDisabled = isParentLoading || isLoading || isToggleAccessLoading;

  let tableSection: ReactElement | null = null;
  if (isError) {
    tableSection = (
      <div className="text-center py-4">
        <em className="icon ni ni-alert-circle fs-3 text-soft d-block mb-2" />
        <p className="text-soft">{t('ErrorLoadingBeneficiariesList')}</p>
      </div>
    );
  } else if (isLoading && data.length === 0) {
    tableSection = (
      <div className="text-center py-5">
        <div className="spinner-border text-primary" role="status">
          <span className="sr-only">{t('Loading')}</span>
        </div>
      </div>
    );
  } else if (rows.length === 0) {
    tableSection = (
      <div className="text-center py-4">
        <em className="icon ni ni-inbox fs-3 text-soft d-block mb-2" />
        <p className="text-soft">{t('IncentivesMarketNoBeneficiariesFound')}</p>
      </div>
    );
  } else {
    tableSection = (
      <>
        <div className="table-responsive card card-bordered">
          <table className="table table-tranx table-hover custom-table mb-0">
            <thead>
              <tr className="tb-tnx-head">
                <th className="tb-tnx-id" style={{ width: '48px' }}>
                  <div className="custom-control custom-checkbox">
                    <input
                      type="checkbox"
                      className="custom-control-input"
                      id="select-all-beneficiaries-page"
                      checked={
                        rows.length > 0 &&
                        selectedBeneficiaryIds.length === rows.length
                      }
                      onChange={handleSelectAll}
                      disabled={switchDisabled}
                    />
                    <label
                      className="custom-control-label"
                      htmlFor="select-all-beneficiaries-page"
                    />
                  </div>
                </th>
                <th className="tb-tnx-id">{t('Name')}</th>
                <th className="tb-tnx-id">{t('Email')}</th>
                <th className="tb-tnx-id">{t('Cif')}</th>
                <th className="tb-tnx-info text-right">{t('IncentivesMarketUnits')}</th>
                <th className="tb-tnx-id text-right">
                  {t('IncentivesMarketAccess')}
                </th>
              </tr>
            </thead>
            <tbody>
              {rows.map(({ beneficiary, equityAccessEnabled }) => (
                <tr key={beneficiary._id} className="tb-tnx-item">
                  <td
                    className="tb-tnx-id align-middle"
                    onClick={(e) => e.stopPropagation()}
                  >
                    <div className="custom-control custom-checkbox">
                      <input
                        type="checkbox"
                        className="custom-control-input"
                        id={`select-beneficiary-${beneficiary._id}`}
                        checked={selectedBeneficiaryIds.includes(beneficiary._id)}
                        onChange={() => handleSelectBeneficiary(beneficiary._id)}
                        disabled={switchDisabled}
                      />
                      <label
                        className="custom-control-label"
                        htmlFor={`select-beneficiary-${beneficiary._id}`}
                      />
                    </div>
                  </td>
                  <td className="tb-tnx-id align-middle">
                    <span className="d-inline-flex align-items-center">
                      <div className="user-avatar user-avatar-xs bg-info-dim mr-1 w-25px h-25px fs-12px">
                        {getPartyImage(beneficiary) ? (
                          <img
                            src={getPartyImage(beneficiary)}
                            alt={beneficiary.name}
                          />
                        ) : (
                          <span>{getPartyInitials(beneficiary)}</span>
                        )}
                      </div>
                      <Link
                        to={`/detalle-beneficiario/${societyId}/${beneficiary._id}`}
                        className="text-primary"
                        onClick={(e) => e.stopPropagation()}
                      >
                        {beneficiary.name}
                      </Link>
                    </span>
                  </td>
                  <td className="tb-tnx-id align-middle">
                    <span className="text-soft">{beneficiary.email || '-'}</span>
                  </td>
                  <td className="tb-tnx-id align-middle">
                    <span className="text-soft">{beneficiary.cif || '-'}</span>
                  </td>
                  <td className="tb-tnx-info text-right align-middle">
                    {formatNumber(beneficiary.sharesCount?.actual ?? 0)}
                  </td>
                  <td
                    className="tb-tnx-id align-middle"
                    onClick={(e) => e.stopPropagation()}
                  >
                    <div className="d-flex justify-content-end">
                      <Switch
                        id={`incentives-market-access-${beneficiary._id}`}
                        checked={equityAccessEnabled}
                        onChange={(checked) => {
                          handleToggleBeneficiary(beneficiary._id, checked).catch(
                            () => undefined
                          );
                        }}
                        disabled={switchDisabled}
                      />
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {paginationData.totalItems > 0 && (
          <DataTablePagination
            className="bg-white border-0"
            totalItems={paginationData.totalItems}
            itemPerPage={paginationData.itemPerPage}
            currentPage={paginationData.currentPage}
            customItemPerPage={paginationData.customItemPerPage}
            paginate={handleChangePage}
            onChangeRowsPerPage={handleChangeRowsPerPage}
            setRowsPerPage={setRowsPerPage}
          />
        )}
      </>
    );
  }

  return (
    <>
      {isToggleAccessLoading && <CustomLoading />}
      <div className="card card-bordered mb-4">
        <div className="card-inner">
          <h6 className="card-title mb-1">
            <em className="icon ni ni-users mr-2 text-primary" />
            {t('BeneficiaryAccess')}
            <span className="text-soft ml-1 fs-14px font-weight-normal">
              ({enabledCount}/{totalPartners || reduxPartners.length || 0})
            </span>
          </h6>
          <p className="text-soft fs-13px mb-4">
            {t('IncentivesMarketBeneficiaryAccessDescription')}
          </p>

          <div className="d-flex flex-wrap justify-content-between align-items-center mb-3">
            <div
              className="form-control-wrap mb-2 mb-md-0"
              style={{ minWidth: '260px' }}
            >
              <div className="form-icon form-icon-left">
                <em className="icon ni ni-search" />
              </div>
              <input
                type="text"
                className="form-control"
                placeholder={[t('name'), t('email'), t('CIF')].join(', ')}
                value={searchText}
                onChange={(e) => setSearchText(e.target.value)}
                disabled={isToggleAccessLoading}
              />
            </div>

            {selectedBeneficiaryIds.length > 0 && (
              <div className="d-flex align-items-center flex-wrap">
                <span className="text-soft fs-12px mx-1">
                  {selectedBeneficiaryIds.length} {t('Selected')}
                </span>
                <Button
                  className="mx-1"
                  variant={ButtonVariants.INFO}
                  disabled={isToggleAccessLoading}
                  onClick={() => handleBulkToggle(true).catch(() => undefined)}
                  outlineStyle
                  dimStyle
                >
                  {t('EnableSelected')}
                </Button>
                <Button
                  className="ml-1"
                  variant={ButtonVariants.DANGER}
                  disabled={isToggleAccessLoading}
                  onClick={() => handleBulkToggle(false).catch(() => undefined)}
                  outlineStyle
                  dimStyle
                >
                  {t('DisableSelected')}
                </Button>
              </div>
            )}
          </div>

          {tableSection}
        </div>
      </div>
    </>
  );
};

export default IncentivesMarketAdminPanelBeneficiaryAccess;
